import api from '../axiosInstance';

/**
 * Returns the currencies available for the authenticated company.
 * @param {object} params - Optional query params: { isEnabled, status }
 * @param {AbortSignal} signal
 * @returns {Promise<{ data: CompanyCurrencyDto[], isEnabled: boolean }>}
 */
export async function getCompanyCurrencies(params = {}, signal) {
  const response = await api.get('CompanyCurrencies', { params, signal });
  return response.data;
}

/**
 * Returns the currently selected company currency code, if available.
 * @param {AbortSignal} signal
 * @returns {Promise<string>}
 */
export async function getSelectedCompanyCurrencyCode(signal) {
  const payload = await getCompanyCurrencies({}, signal);
  const currencies = payload?.data ?? payload ?? [];
  const selected = currencies.find((currency) => currency?.isSelected);
  return selected?.code ? `${selected.code}`.trim().toUpperCase() : '';
}

/**
 * Returns one company currency by id.
 * @param {number} id - Company currency id
 * @param {AbortSignal} signal
 * @returns {Promise<CompanyCurrencyDto>}
 */
export async function getCompanyCurrencyById(id, signal) {
  const response = await api.get(`CompanyCurrencies/${id}`, { signal });
  return response.data;
}

/**
 * Creates a new currency for the authenticated company.
 * @param {{ name: string, symbol?: string, locale?: string, code?: string }} data
 * @param {AbortSignal} signal
 * @returns {Promise<CompanyCurrencyDto>}
 */
export async function createCompanyCurrency(data, signal) {
  const response = await api.post('CompanyCurrencies', data, { signal });
  return response.data;
}

/**
 * Updates a company currency's custom name and/or enabled state.
 * @param {number} id - Company currency id
 * @param {{ customName?: string, isEnabled: boolean }} data
 * @param {AbortSignal} signal
 * @returns {Promise<CompanyCurrencyDto>}
 */
export async function updateCompanyCurrency(id, data, signal) {
  const response = await api.put(`CompanyCurrencies/${id}`, data, { signal });
  return response.data;
}

/**
 * Marks the specified company currency as the selected currency.
 * @param {number} id - Company currency id
 * @param {AbortSignal} signal
 * @returns {Promise<CompanyCurrencyDto>}
 */
export async function selectCompanyCurrency(id, signal) {
  const response = await api.put(`CompanyCurrencies/${id}/select`, null, { signal });
  return response.data;
}

/**
 * Disables (soft-deletes) the specified company currency.
 * @param {number} id - Company currency id
 * @param {AbortSignal} signal
 * @returns {Promise<{ message: string }>}
 */
export async function deleteCompanyCurrency(id, signal) {
  const response = await api.delete(`CompanyCurrencies/${id}`, { signal });
  return response.data;
}
